import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Plus, MoreHorizontal, Star, Calendar, Flag, Loader2,
  GripVertical, Check, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { format, parseISO, isToday, isPast } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTasksContext } from '../TasksLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_COLUMNS = [
  { id: 'todo', name: 'À faire', status: 'todo', color: '#6B7280' },
  { id: 'in_progress', name: 'En cours', status: 'in_progress', color: '#3B82F6' },
  { id: 'done', name: 'Terminé', status: 'done', color: '#10B981' }
];

const PRIORITIES = {
  critical: { color: '#EF4444', icon: '🔴' },
  high: { color: '#F59E0B', icon: '🟠' },
  normal: { color: '#3B82F6', icon: '🔵' },
  low: { color: '#6B7280', icon: '⚪' }
};

export default function TasksKanban() {
  const { listId } = useParams();
  const { refreshData } = useTasksContext() || {};
  
  const [list, setList] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const token = localStorage.getItem('token');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch list info
      if (listId) {
        const listRes = await fetch(`${API_URL}/api/task-lists/${listId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const listData = await listRes.json();
        setList(listData);
        
        if (listData.kanban_columns?.length > 0) {
          setColumns(listData.kanban_columns);
        }
      }
      
      // Fetch tasks
      const endpoint = listId 
        ? `/api/task-lists/${listId}/tasks?include_completed=true`
        : '/api/tasks?include_completed=true';
      
      const tasksRes = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tasksData = await tasksRes.json();
      setTasks(tasksData.tasks || []);
    } catch (err) {
      console.error('Error fetching kanban data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [listId]);

  const getTasksByStatus = (status) => {
    return tasks.filter(t => t.status === status);
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    
    if (!destination) return;
    
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) return;
    
    const taskId = draggableId;
    const newStatus = destination.droppableId;
    
    // Optimistic update
    setTasks(prev => prev.map(t => 
      t.id === taskId ? { ...t, status: newStatus } : t
    ));
    
    // Update on server
    try {
      await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (newStatus === 'done') {
        await fetch(`${API_URL}/api/tasks/${taskId}/complete`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      refreshData?.();
    } catch (err) {
      console.error('Error updating task status:', err);
      fetchData(); // Revert on error
    }
  };

  const createTask = async (status) => {
    if (!newTaskTitle.trim()) return;
    
    try {
      await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newTaskTitle,
          list_id: listId || null,
          status: status
        })
      });
      
      setNewTaskTitle('');
      setShowAddTask(null);
      fetchData();
      refreshData?.();
    } catch (err) {
      console.error('Error creating task:', err);
    }
  };

  const toggleImportant = async (taskId, e) => {
    e?.stopPropagation();
    try {
      await fetch(`${API_URL}/api/tasks/${taskId}/toggle-important`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Error toggling important:', err);
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      refreshData?.();
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3">
          {list && (
            <div 
              className="w-4 h-4 rounded"
              style={{ backgroundColor: list.color }}
            />
          )}
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {list?.name || 'Vue Kanban'}
          </h1>
          <Badge variant="secondary">{tasks.length} tâches</Badge>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto p-4">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 h-full min-w-max">
            {columns.map((column) => {
              const columnTasks = getTasksByStatus(column.status);
              
              return (
                <div 
                  key={column.id}
                  className="w-80 flex flex-col bg-gray-100 dark:bg-gray-800 rounded-lg"
                >
                  {/* Column Header */}
                  <div 
                    className="flex-shrink-0 p-3 flex items-center justify-between border-b-2"
                    style={{ borderColor: column.color }}
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: column.color }}
                      />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {column.name}
                      </span>
                      <span className="text-sm text-gray-500">
                        {columnTasks.length}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setShowAddTask(column.status)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Tasks */}
                  <Droppable droppableId={column.status}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={cn(
                          "flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px]",
                          snapshot.isDraggingOver && "bg-blue-50 dark:bg-blue-900/20"
                        )}
                      >
                        {columnTasks.map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={cn(
                                  "bg-white dark:bg-gray-700 rounded-lg p-3 shadow-sm border border-gray-200 dark:border-gray-600 cursor-pointer hover:shadow-md transition-shadow",
                                  snapshot.isDragging && "shadow-lg ring-2 ring-blue-500"
                                )}
                              >
                                {/* Task Header */}
                                <div className="flex items-start justify-between gap-2">
                                  <p className={cn(
                                    "text-sm font-medium text-gray-900 dark:text-white flex-1",
                                    task.status === 'done' && "line-through text-gray-400"
                                  )}>
                                    {task.title}
                                  </p>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded">
                                        <MoreHorizontal className="h-4 w-4 text-gray-400" />
                                      </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => toggleImportant(task.id)}>
                                        <Star className="h-4 w-4 mr-2" />
                                        {task.is_important ? 'Retirer important' : 'Marquer important'}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => deleteTask(task.id)}
                                        className="text-red-600"
                                      >
                                        Supprimer
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>

                                {/* Task Meta */}
                                <div className="flex items-center gap-2 mt-2">
                                  {task.due_date && (
                                    <span className={cn(
                                      "text-xs flex items-center gap-1",
                                      isPast(parseISO(task.due_date)) && task.status !== 'done'
                                        ? "text-red-500"
                                        : "text-gray-500"
                                    )}>
                                      <Calendar className="h-3 w-3" />
                                      {isToday(parseISO(task.due_date)) 
                                        ? "Aujourd'hui"
                                        : format(parseISO(task.due_date), 'dd MMM', { locale: fr })}
                                    </span>
                                  )}
                                  {task.priority && task.priority !== 'normal' && (
                                    <span className="text-xs">
                                      {PRIORITIES[task.priority]?.icon}
                                    </span>
                                  )}
                                  {task.is_important && (
                                    <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                                  )}
                                </div>

                                {/* Subtasks indicator */}
                                {task.subtasks?.length > 0 && (
                                  <div className="mt-2 text-xs text-gray-500">
                                    <Check className="h-3 w-3 inline mr-1" />
                                    {task.subtasks.filter(s => s.status === 'done').length}/{task.subtasks.length}
                                  </div>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        
                        {/* Quick Add in Column */}
                        {showAddTask === column.status && (
                          <div className="bg-white dark:bg-gray-700 rounded-lg p-3 shadow-sm border border-blue-300">
                            <Input
                              placeholder="Titre de la tâche..."
                              value={newTaskTitle}
                              onChange={(e) => setNewTaskTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') createTask(column.status);
                                if (e.key === 'Escape') {
                                  setShowAddTask(null);
                                  setNewTaskTitle('');
                                }
                              }}
                              autoFocus
                            />
                            <div className="flex gap-2 mt-2">
                              <Button size="sm" onClick={() => createTask(column.status)}>
                                Ajouter
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                  setShowAddTask(null);
                                  setNewTaskTitle('');
                                }}
                              >
                                Annuler
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
}
